import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { generateEmailThreadSummary } from '../../../api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import {
  getPageBrowserTabTitleWithEntityName,
  useAsyncCallback,
  useNotification,
  usePageBrowserTabTitle,
} from '../../../hooks'
import { getAsyncTaskId } from '../../../utils/async'
import { AITextGenerationLocalContextType } from '../../AITextGeneration'
import { toggleEmailRead, updateEmail } from '../Email.api'
import { useMassMailingAsyncAction } from '../Email.hooks'
import { EmailProfile } from '../EmailProfile'

import { useEmailThreadProfileLoaders } from './EmailThreadProfile.hooks'
import {
  getEmailThreadProfileActions,
  getEmailThreadProfileActionsWidgetItems,
} from './EmailThreadProfile.utils'

function EmailThreadProfile({
  actionsContextProps,
  getProfileData,
  headerProps,
  mailUuid,
  navigation = null,
  profileLoadersRef,
  withWidgets = true,
  onReload,
  onSuccessRead,
  ...restProps
}) {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const { handleAddSuccessNotification } = useNotification()

  const profileLoaders = useEmailThreadProfileLoaders({
    getProfileData,
    isReadEnabled: !navigation?.isLoading,
    mailUuid,
    withWidgets,
    onSuccessRead,
  })

  const {
    entityData,
    getMessageThresholdItemsProps,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    loadAISummaryWidgetData,
    loadEmailMessages,
    loadingMoreMessage,
    loadWidgetsData,
    messages,
    serverError,
    setSummaryWidgetData,
    summaryData,
    widgetsData,
  } = profileLoaders

  useEffect(() => {
    if (profileLoadersRef) {
      profileLoadersRef.current = profileLoaders
    }
  }, [profileLoaders])

  usePageBrowserTabTitle({
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: entityData?.subject,
      title: t('BrowserTab.EmailThreadProfileTabTitleKey'),
    }),
  })

  useEffect(() => {
    if (isInitiated) {
      loadEmailMessages()
    }
  }, [isInitiated])

  async function updateMessage({ id, ...restData }, successTitle) {
    return updateEmail(id, restData, true).then(() => {
      handleAddSuccessNotification(successTitle)
    })
  }

  async function handleMarkAsUnread() {
    return toggleEmailRead({ uuid: mailUuid })
  }

  const { checkAsyncMassMailingTaskStatus } =
    useMassMailingAsyncAction(EMPTY_OBJECT)

  const handleFinishMassMailingReply = useAsyncCallback(() => {
    onReload?.()

    return handleReloadProfile()
  }, [handleReloadProfile, onReload])

  async function handleSuccessReply({ data }) {
    const taskId = getAsyncTaskId(data)

    if (taskId) {
      checkAsyncMassMailingTaskStatus(
        { ...data, task: taskId },
        handleFinishMassMailingReply
      )

      return
    }

    if (data.thread === mailUuid) {
      onReload?.()

      return handleReloadProfile()
    }

    return navigate(PROFILE_ROUTE_NAMES.emailThreadNG({ id: data.thread }))
  }

  return (
    <EmailProfile
      {...restProps}
      actionsContextProps={{
        ...actionsContextProps,
        getPrintPageLink: PROFILE_ROUTE_NAMES.emailThreadPrint,
        onUnread: handleMarkAsUnread,
      }}
      entityData={entityData}
      headerProps={{
        ...headerProps,
        getActions: getEmailThreadProfileActions,
      }}
      isInitiated={isInitiated && messages !== null}
      isLoading={isLoading}
      loadWidgetsData={loadWidgetsData}
      mailUuid={mailUuid}
      messages={messages}
      messagesListProps={{
        getMessageThresholdItemsProps: getMessageThresholdItemsProps,
        itemProps: {
          actionsConfig: actionsContextProps,
          aiLocalContextType: AITextGenerationLocalContextType.EmailReply,
          replyConfig: { onSuccess: handleSuccessReply },
        },
        loadingMore: loadingMoreMessage,
      }}
      navigation={navigation}
      serverError={serverError}
      summaryProps={{
        data: summaryData,
        generateSummaryParams: { thread_id: mailUuid },
        onError: setSummaryWidgetData,
        onGenerateSummary: generateEmailThreadSummary,
        onReloadData: loadAISummaryWidgetData,
        onSuccess: setSummaryWidgetData,
      }}
      widgetsData={widgetsData}
      widgetsProps={{
        actionsProps: {
          getActions: getEmailThreadProfileActionsWidgetItems,
          isThread: true,
        },
      }}
      withWidgets={withWidgets}
      onReloadEntity={handleReloadProfile}
      onReloadMessages={loadEmailMessages}
      onUpdateEntity={handleUpdateEntity}
      onUpdateMessage={updateMessage}
    />
  )
}

export default EmailThreadProfile
